import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getPatients, subscribeToPatients } from '../services/cloudflareService';

export const usePatientsListSync = (hospitalId: string | undefined | null) => {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ['patients', hospitalId],
        queryFn: () => hospitalId ? getPatients(hospitalId) : [],
        enabled: !!hospitalId,
    });

    useEffect(() => {
        if (!hospitalId) return;

        const unsubscribe = subscribeToPatients(hospitalId, (updatedPatients) => {
            if (Array.isArray(updatedPatients)) {
                queryClient.setQueryData(['patients', hospitalId], updatedPatients);
            }
        });

        const handleOptimisticUpdate = (event: Event) => {
            const customEvent = event as CustomEvent;
            const updatedPatient = customEvent.detail;
            if (!updatedPatient) return;
            
            queryClient.setQueryData(['patients', hospitalId], (old: any) => {
                if (!Array.isArray(old)) return old;
                // If patient exits and shouldn't be in list right away, we could filter, 
                // but usually active patients stay in the list until hard refresh or SSE confirms
                return old.map(p => p.id === updatedPatient.id ? updatedPatient : p);
            });
        };

        window.addEventListener('patient-updated-optimistically', handleOptimisticUpdate);

        return () => {
            unsubscribe();
            window.removeEventListener('patient-updated-optimistically', handleOptimisticUpdate);
        };
    }, [hospitalId, queryClient]);

    return query;
};
